<template>
  <div id="app" v-if="isLoaded">
    <navigation />
    <warning />
    <div class="wrapper">
      <transition name="fade" mode="out-in">
        <router-view :key="$route.fullPath" />
      </transition>
    </div>
    <foot />
  </div>
</template>

<script>
const Navigation = () =>
  import(/* webpackChunkName: "Navigation" */ "@/components/Navigation");
const Warning = () =>
  import(/* webpackChunkName: "Warning" */ "@/components/Warning");
const Foot = () =>
  import(/* webpackChunkName: "Footer" */ "@/components/Footer");
export default {
  metaInfo: {
    title: "",
    titleTemplate: "Aspekt Office %s",
  },
  components: {
    Navigation,
    Warning,
    Foot,
  },
  data() {
    return {
      isLoaded: false,
    };
  },
  beforeCreate() {
    this.$store.dispatch("loadSite").then(() => {
      this.isLoaded = true;
    });
  },
};
</script>

<style lang="scss">
@import "node_modules/reset-css/sass/_reset";

@font-face {
  font-family: "Modern-Aspekt-Regular";
  src: url("/dist/assets/ModernGothicAspektOfficeHeadline-Regular.woff")
      format("woff"),
    url("/dist/assets/ModernGothicAspektOfficeHeadline-Regular.woff2")
      format("woff2");
}
@font-face {
  font-family: "Modern-Aspekt-Medium";
  src: url("/dist/assets/ModernGothicAspektOfficeHeadline-Medium.woff")
      format("woff"),
    url("/dist/assets/ModernGothicAspektOfficeHeadline-Medium.woff2")
      format("woff2");
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

a,
a:active,
a:hover,
a:visited,
.link {
  color: black;
  text-decoration: none;
  transition: color 0.5s;
  cursor: pointer;
  &:hover {
    color: #aaa;
  }
}

body,
html {
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  --fs: 1.05rem;
  --lh: calc(1.15 * 1.05rem);
  @media screen and (max-width: 768px) {
      --fs: 1rem;
      --lh: calc(1.15 * 1rem);
  }
  font-size: var(--fs);
  line-height: var(--lh);
}

body,
html {
  font-family: "Modern-Aspekt-Regular", sans-serif;
  font-feature-settings: "ss01", "ss02";
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  margin: 0;
  // min-height: 100vh;
}

.pad {
  padding: 0 calc(2 * var(--lh));
}

#app {
  .wrapper {
    min-height: 100vh;
  }
  main {
    padding-top: 0;
  }
  main.projects {
    padding-top: calc(2 * var(--lh));
  }
  li,
  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  img {
    display: block;
    max-width: 100%;
    object-fit: contain;
    &.blur {
      filter: blur(10px) saturate(100%);
    }
    &.bg {
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      object-fit: cover;
    }
  }
  .pt {
    padding-top: calc(0.5 * var(--lh));
  }
  .mb {
    margin-bottom: calc(2.8 * var(--lh));
  }
  h1 {
    font-weight: normal;
    font-family: "Modern-Aspekt-Medium", sans-serif;
    font-size: calc(3 * var(--fs));
    line-height: 1;
    p:not(:last-of-type) {
      margin-bottom: calc(2.8 * var(--lh));
    }
    @media only screen and (max-width: 1024px) {
      font-size: calc(2 * var(--fs));
      line-height: 1;
      p:not(:last-of-type) {
        margin-bottom: calc(1.8 * var(--lh));
      }
    }
  }
  h2 {
    font-family: "Modern-Aspekt-Medium", sans-serif;
    font-size: calc(2 * var(--fs));
    line-height: calc(1.8 * var(--lh));
    @media only screen and (max-width: 1024px) {
      font-size: calc(1 * var(--fs));
      line-height: calc(1 * var(--lh));
    }
  }
  h3,
  em,
  medium {
    font-family: "Modern-Aspekt-Medium", sans-serif;
  }
  .caption {
    font-size: calc(0.8 * var(--fs));
    line-height: 1.1;
    letter-spacing: 0.025rem;
  }
  .uppercase {
    text-transform: uppercase;
    letter-spacing: 0.1em;
  }
  .tac {
    text-align: center;
  }
  .title {
    position: absolute;
    top: 1px;
    left: calc(-0.125 * var(--lh));
    transform-origin: top left;
    transform: rotate(90deg);
    white-space: nowrap;
    @media only screen and (max-width: 768px) {
      display: none;
    }
  }
  .pad-right {
    padding-right: calc(2 * var(--lh));
  }
  .txt-r {
    text-align: right;
  }
  .quote-left {
    text-align: left;
    align-self: center;
    // padding: calc(2 * var(--lh));
  }
  .quote {
    text-align: center;
    align-self: center;
    padding: calc(6 * var(--lh));
  }
  .medium {
    font-family: "Modern-Aspekt-Medium", sans-serif;
  }
  section {
    box-sizing: border-box;
    width: 100vw;
    &.more-projects {
      padding: calc(2 * var(--lh)) calc(2 * var(--lh));
      margin: calc(2 * var(--lh)) 0 0;
    }
    &.col-center {
      display: grid;
      grid-template-columns: 1fr 2fr 1fr;
      grid-gap: var(--lh);
      padding: calc(2 * var(--lh)) calc(2 * var(--lh));
      margin: calc(2 * var(--lh)) 0;
    }
    &.pad {
      padding: 1rem 2.5rem;
    }
    &.pad2 {
      padding: calc(2 * var(--lh));
    }
    &.pad-x {
      padding: 0 2.5rem;
    }
    &.pad-b {
      padding-bottom: 5rem;
    }
    &.pad-t {
      padding-top: 5rem;
    }
    .w-text {
      max-width:50ch;
      margin:0 auto;
      text-align:center;
      line-height: 1.25;
    }
    &.col-2 {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-gap: var(--lh);
      padding: calc(2 * var(--lh)) calc(2 * var(--lh));
      margin: calc(2 * var(--lh)) 0;
      &.large {
        padding: 0;
        grid-gap: calc(4 * var(--lh));
      }
      div {
        grid-column: span 1;
      }
      @media only screen and (max-width: 1024px) {
        grid-template-columns: 1fr;
      }
      &#categories {
        margin-bottom: calc(6 * var(--lh));
      }
    }
    &.col-3 {
      display: grid;
      grid-template-columns: 2fr 1fr 1fr;
      grid-gap: var(--lh);
      padding: calc(2 * var(--lh)) calc(2 * var(--lh));
      margin: calc(2 * var(--lh)) 0;
      div {
        grid-column: span 1;
      }
      @media only screen and (max-width: 1024px) {
        grid-template-columns: 1fr;
      }
    }
    &.col-center {
      display: grid;
      display: grid;
      grid-template-columns: 1fr 3fr 1fr;
      grid-gap: var(--lh);
      padding: calc(2 * var(--lh)) calc(2 * var(--lh));
      margin: calc(2 * var(--lh)) 0;
      @media only screen and (max-width: 768px) {
        grid-template-columns: 1fr 5fr 1fr;
        br {
          display: none;
        }
      }
    }
    &.col-three {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      grid-gap: var(--lh);
      padding: calc(2 * var(--lh)) calc(2 * var(--lh));
      margin: calc(2 * var(--lh)) 0;
      &.client-list {
        grid-gap: calc(3 * var(--lh));
        grid-template-columns: 1fr 1fr 1fr;
        align-items: center;
        padding-top: var(--lh);
        margin-bottom: calc(6 * var(--lh));
        margin-top: 0;
      }
      div {
        grid-column: span 1;
      }
      @media only screen and (max-width: 1024px) {
        grid-template-columns: 1fr;
      }
    }
    &.col-4 {
      display: grid;
      grid-template-columns: 3fr 1fr;
      grid-gap: var(--lh);
      padding: calc(2 * var(--lh)) calc(2 * var(--lh));
      margin: calc(2 * var(--lh)) 0;
      div {
        grid-column: span 1;
      }
      @media only screen and (max-width: 1024px) {
        grid-template-columns: 1fr;
      }
    }
    &.col-four {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr 1fr;
      grid-gap: var(--lh);
      padding: calc(2 * var(--lh)) calc(2 * var(--lh));
      margin: calc(2 * var(--lh)) 0;
      &.next-projects {
        margin-top: 0;
      }
      div {
        grid-column: span 1;
      }
      @media only screen and (max-width: 1024px) {
        grid-template-columns: 1fr;
      }
    }
    &.tight {
      padding: 0 calc(2 * var(--lh));
      margin: 0 0;
    }
    &.slideshow {
      margin: calc(1 * var(--lh)) 0;
      .swiper-slide {
        width: auto;
        margin: 0 calc(0.5 * var(--lh));
      }
      .swiper-button-next {
        right: 0;
        font-size: calc(2 * var(--fs));
      }
      .swiper-button-prev {
        left: 0;
        font-size: calc(2 * var(--fs));
      }
      .swiper-button-next,
      .swiper-button-prev {
        font-family: "Modern-Aspekt-Regular", sans-serif;
        background: none;
        width: auto;
        height: auto;
        margin-top: 0;
        outline: none;
        padding: 0 calc(2 * var(--lh));
        transform: translateY(-50%);
      }
      .slide {
        position: relative;
        display: inline-block;
        height: 80vh;
        @media only screen and (max-width: 1024px) {
          height: 55vh;
        }
      //   @media only screen and (max-width: 768px) {
      //     height: 45vh;
      //   }
      }
      img {
        display: block;
        width: auto;
        height: 100%;
      }
    }
  }
}
</style>
